import { Store, useStore } from 'vuex';

import { LineItem, LineItemProperty, Package, PackageProperty, Plan, PlanProperty } from '@/common/models';

interface IEntityUpdateHandler {
  updateEntityProperties(properties: LineItemProperty[] | PlanProperty[] | PackageProperty[]): Promise<void>;
  removeEntityProperties(propertyKeys: string[]): Promise<void>;
}

export const entityPropertyModels = {
  Plan: PlanProperty,
  LineItem: LineItemProperty,
  Package: PackageProperty,
};

export default class EntityPropertyUpdateHandler implements IEntityUpdateHandler {
  protected entity: LineItem | Plan | Package;
  protected store: Store<unknown>;

  constructor(entity: LineItem | Plan | Package, store = useStore()) {
    this.entity = entity;
    this.store = store;
  }

  protected get entityType(): string {
    switch (this.entity.constructor) {
      case Plan:
        return 'plan';
      case LineItem:
        return 'lineItem';
      case Package:
        return 'package';
      default:
        throw new Error('Invalid entity type provided');
    }
  }

  private get propertiesKey(): string {
    return `${this.entityType}Properties`;
  }

  private updateEntity(value: LineItemProperty[] | PlanProperty[] | PackageProperty[]): Promise<void> {
    const action = `update${this.entity.type}Properties`;
    return this.store.dispatch(action, {
      [this.entityType]: this.entity,
      value,
    });
  }

  removeEntityProperties(propertyKeys: string[]): Promise<void> {
    const filteredProperties = this.entity[this.propertiesKey].filter(
      (x: LineItemProperty | PlanProperty | PackageProperty) => !propertyKeys.includes(x.key),
    );
    return this.updateEntity(filteredProperties);
  }

  updateEntityProperties(properties: LineItemProperty[] | PlanProperty[] | PackageProperty[]): Promise<void> {
    const updatedKeys = properties.map((property: LineItemProperty | PlanProperty | PackageProperty) => property.key);
    const updatedProperties = [
      ...this.entity[this.propertiesKey].filter(
        (property: LineItemProperty | PlanProperty | PackageProperty) => !updatedKeys.includes(property.key),
      ),
      ...properties,
    ];
    return this.updateEntity(updatedProperties);
  }

  updateEntityProperty(key: string, value: string): Promise<void> {
    return this.updateEntityProperties([new entityPropertyModels[this.entity.type](null, null, key, value)]);
  }

  getEntityProperty(key: string): LineItemProperty | PlanProperty | PackageProperty | null {
    return this.entity?.[`${this.entityType}Properties`].find(
      (property: LineItemProperty | PlanProperty | PackageProperty) => property.key === key,
    );
  }
}
